import Card from "components/DesignSystem/Card/Card";
import { PlusIcon } from "components/icons/PlusIcon";
import { Preview } from "components/PreviewModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  FinancialClosing,
  useAddOtherFinancialsMutation,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { FINANCIALS_FOLDER_NAME } from "constants/documents";
import { CheckListAccordion } from "./CheckListAccordion";
import { OtherAttachmentContent } from "./OtherAttachmentContent";
import { Button } from "components/DesignSystem/Button/Button";

export const OtherAttachmentSingle = ({
  other_financials,
  isEditAllowed,
  currentStatus,
  isCreatedByAdmin,
  isFinancialVersionV1,
  year,
}: {
  other_financials: FinancialClosing["other_financials"];
  isEditAllowed: boolean;
  currentStatus: string;
  isCreatedByAdmin: boolean;
  isFinancialVersionV1?: boolean;
  year: number;
}) => {
  const folderPath = [String(year), FINANCIALS_FOLDER_NAME];
  const [addOtherFinancials, { isLoading }] = useAddOtherFinancialsMutation();
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewModal = useModal();
  const { isCpa } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const onCreateEmptyAttachments = () => {
    try {
      if (isLoading) return;
      const isInValidAttachmentsToCreateNew = other_financials?.some(
        (item) => !item.document && !item.url
      );
      if (isInValidAttachmentsToCreateNew) {
        alertToast({
          message: "Please fill in all required attachment fields.",
          title: "Incomplete Attachment",
        });
        return;
      }
      addOtherFinancials({
        entityId: entityId,
        financialClosingId: financialClosingId,
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onFileClick = ({ uuid }: { uuid: string }) => {
    setPreviewId(uuid);
    previewModal.open();
  };

  const onPreviewClose = () => {
    setPreviewId(null);
    previewModal.close();
  };

  const showOtherAttachments = other_financials && other_financials.length > 0;

  const isEditValid =
    isEditAllowed &&
    hasWritePermission &&
    ((isCreatedByAdmin && isCpa) || !isCreatedByAdmin);

  return (
    <div>
      <div className="t-mb-4">
        <Card.Root className="t-rounded-lg sm:!t-px-3" variant="primary">
          <Card.Body>
            <CheckListAccordion.Root
              type="multiple"
              defaultValue={["OtherAttachments"]}
            >
              <CheckListAccordion.Item value="OtherAttachments">
                <CheckListAccordion.Trigger>
                  <span className="t-text-subtext">Other Attachments</span>
                </CheckListAccordion.Trigger>
                <CheckListAccordion.Content>
                  <div
                    className={
                      "t-flex t-flex-col t-py-4 t-gap-4 t-w-full t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparen t-border-t"
                    }
                  >
                    {showOtherAttachments &&
                      other_financials.map((item, index) => (
                        <OtherAttachmentContent
                          key={item.uuid}
                          index={index}
                          item={item}
                          onFileClick={() =>
                            onFileClick({ uuid: item.document?.uuid! })
                          }
                          isEditValid={isEditValid}
                          entityId={entityId}
                          folderPath={folderPath}
                        />
                      ))}

                    {previewId && (
                      <Preview
                        closeModal={onPreviewClose}
                        showModal={previewModal.isOpen}
                        previewId={previewId}
                      />
                    )}
                    {isEditAllowed && (
                      <div className="t-w-fit">
                        <Button
                          customType="ghost"
                          leftIcon={<PlusIcon size="12" />}
                          onClick={onCreateEmptyAttachments}
                        >
                          <div className="t-text-subtext-sm t-text-text-60">
                            Add Attachment
                          </div>
                        </Button>
                      </div>
                    )}
                  </div>
                </CheckListAccordion.Content>
              </CheckListAccordion.Item>
            </CheckListAccordion.Root>
          </Card.Body>
        </Card.Root>
      </div>
    </div>
  );
};
